<template>
  <component
    :is="formattedType"
    class="button-classic"
    :class="btnClasses"
    v-bind="attributes"
    @click="$emit('click')"
  >
    <span
      v-if="displayLeftIcon"
      class="icon"
      :class="icon"
    >
      <!-- @slot Emplacement pour une icône à gauche du label (doit être un SVG). -->
      <slot name="left-icon" />
    </span>
    <span
      v-if="label"
      class="label bold"
      :class="labelClass"
      v-html="label"
    />
    <!-- @slot Emplacement pour un label. -->
    <slot
      v-else
      name="label"
    />
    <span
      v-if="displayRightIcon"
      class="icon"
      :class="icon"
    >
      <!-- @slot Emplacement pour une icône à droite du label (doit être un SVG). -->
      <slot name="right-icon" />
    </span>
    <!-- @slot Emplacement pour d'autres éléments. -->
    <slot />
  </component>
</template>

<script>
import { defineComponent } from "vue";
/**
 * Affiche un bouton standard pouvant contenir du texte et des icônes.
 * S'il n'y a pas de texte et que l'on souhaite afficher une icône, on peut utiliser au choix les slots
 * `left-icon` ou `right-icon`.
 */
export default defineComponent({
  name: "ButtonClassic",
  props: {
    /**
     * Indique le type de balise (bouton ou lien).
     */
    baliseType: {
      type: String,
      validator: (value) => ["button", "a", "router-link"].includes(value),
      default: "button",
    },
    /**
     * Attribut "type" de la balise "button" si le bouton est une balise "button".
     */
    type: {
      type: String,
      default: "button",
    },
    /**
     * Texte dans le bouton.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Définit une apparence alternative (bordure, fond...).
     */
    variant: {
      type: String,
      validator: (value) => ["simple", "ghost", "solid", "special"].includes(value),
      default: "simple",
    },
    /**
     * Spécifie un ensemble de couleurs.
     * "black" n'est utilisable qu'avec la variante "special", et "warning".
     */
    color: {
      type: String,
      validator: (value) => ["white", "black", "primary", "secondary", "positive", "warning", "error"].includes(value),
      default: "white",
    },
    /**
     * Affiche une icône dans le bouton à un emplacement donné.
     */
    icon: {
      type: String,
      validator: (value) => ["left", "right", "both", "none"].includes(value),
      default: "none",
    },
    /**
     * Taille globale.
     */
    size: {
      type: String,
      validator: (value) => ["small", "medium", "large"].includes(value),
      default: "medium",
    },
    /**
     * Désactive le bouton.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Lien externe si le bouton est une balise "a".
     */
    extLink: {
      type: String,
      default: null,
    },
    /**
     * Informations de la route si le bouton est une balise <router-link>.
     */
    routerLinkObj: {
      type: Object,
      default: null,
    },
    /**
     * Ouvre le lien dans un nouvel onglet.
     */
    targetBlank: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché au clic sur le bouton.
     */
    "click",
  ],
  computed: {
    btnClasses() {
      return {
        [this.variant]: true,
        [this.color]: true,
        [this.size]: true,
        "only-icon": !this.label && !this.$slots.label && this.icon !== "none",
        "only-label": (this.label || this.$slots.label) && this.icon === "none",
      };
    },
    labelClass() {
      return {
        "text-small": this.size === "small",
        "text-medium": this.size === "medium",
        s6: this.size === "large",
        right: this.icon === "left",
        left: this.icon === "right",
        center: this.icon === "both",
      };
    },
    displayLeftIcon() {
      const condition = this.icon !== "none" && (this.icon === "left" || this.icon === "both");
      return condition;
    },
    displayRightIcon() {
      const condition = this.icon !== "none" && (this.icon === "right" || this.icon === "both");
      return condition;
    },
    formattedType() {
      return this.disabled ? "button" : this.baliseType;
    },
    attributes() {
      switch (this.formattedType) {
        case "a":
          return {
            href: this.extLink,
            target: "_blank",
            rel: "noreferrer noopener",
          };

        case "router-link":
          return {
            to: this.routerLinkObj,
            // TODO: à vérifier quand on aura un cas concrêt
            target: this.targetBlank ? "_blank" : undefined,
          };

        default:
          return {
            type: this.type,
            disabled: this.disabled,
          };
      }
    },
  },
});
</script>

<style lang="scss">
@use "../../assets/styles/components/buttons/button_classic.scss";
</style>
